import { useCallback } from 'react';
import { badgeStyle, btn, divider, emptyBox, kicker, pill, segOpt, settle, t } from '../styles';
import { CardArc } from '../components/Chrome';
import { VenueMap } from '../components/VenueMap';
import type { SelectedBooth } from '@ce/core';
import type { ScreenProps } from './types';

export function Events({ bundle, state, dispatch }: ScreenProps) {
  return (
    <div className="stagger">
      <p style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.55), margin: '0 0 16px' }}>
        {bundle.strings.eventsIntro}
      </p>
      <div style={{ display: 'grid', gap: 10, position: 'relative' }}>
        {bundle.specialEvents.map((e, i) => {
          const on = state.saved.includes(e.ref);
          return (
            <div
              key={e.id}
              style={{
                borderRadius: 10, background: 'var(--color-surface)',
                boxShadow: 'var(--shadow-sm)', overflow: 'hidden', ...settle(i),
              }}
            >
              <div style={{ padding: '15px 15px 13px', position: 'relative' }}>
                <CardArc size={160} top={-60} right={-50} opacity={0.13} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={kicker(9.5, 'var(--color-accent-300)', 0.14)}>{e.kicker}</span>
                  <span style={badgeStyle(e.hot)}>{e.status}</span>
                </div>
                <div
                  style={{
                    font: '500 17px/1.2 var(--font-body)', margin: '9px 0 7px',
                    letterSpacing: '-0.015em',
                  }}
                >
                  {e.title}
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.6) }}>{e.desc}</div>
                <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 11.5, color: t(0.45) }}>
                  <span>{e.when}</span>
                  <span>{e.room}</span>
                  <span>{e.price}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, padding: '11px 15px', borderTop: `1px solid ${divider}` }}>
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'toggleSave', id: e.ref })}
                  style={btn('primary', { fontSize: 12.5 })}
                >
                  {on ? '★ In my schedule' : '☆ Add to schedule'}
                </button>
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'locate', level: e.level })}
                  style={btn('secondary', { fontSize: 12.5 })}
                >
                  Locate
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ height: 26 }} />
    </div>
  );
}

export function Hall({ bundle, state, dispatch }: ScreenProps) {
  const zoneKeys = ['', ...Object.keys(bundle.venue.zones)];

  const handleSelect = useCallback(
    (booth: SelectedBooth | null) => dispatch({ type: 'setBooth', booth }),
    [dispatch],
  );

  const zoneKeyOf: Record<string, string> = Object.fromEntries(
    bundle.exhibitorZones.filter((z) => z.key !== 'all').map((z) => [z.label, z.key]),
  );
  const exhibitors = bundle.exhibitors.filter(
    (x) => state.exZone === 'All' || x.zone === zoneKeyOf[state.exZone],
  );

  return (
    <div className="stagger">
      <div
        style={{
          display: 'inline-flex', border: `1px solid ${divider}`, borderRadius: 8,
          overflow: 'hidden', marginBottom: 14,
        }}
      >
        {(['map', 'exhibitors'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => dispatch({ type: 'setHallTab', tab })}
            aria-pressed={state.hall === tab}
            style={segOpt(state.hall === tab)}
          >
            {tab === 'map' ? '3D map' : 'Exhibitors'}
          </button>
        ))}
      </div>

      {state.hall === 'map' ? (
        <div>
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, marginBottom: 10,
            }}
          >
            <div
              style={{
                display: 'inline-flex', border: `1px solid ${divider}`, borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {bundle.venue.levels.map((lvl) => (
                <button
                  key={lvl.id}
                  type="button"
                  onClick={() => dispatch({ type: 'setMapLevel', level: lvl.id })}
                  aria-pressed={state.mapLevel === lvl.id}
                  style={segOpt(state.mapLevel === lvl.id)}
                >
                  {lvl.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10.5, color: t(0.38) }}>{bundle.strings.mapHint}</div>
          </div>

          <div
            style={{
              borderRadius: 12, overflow: 'hidden', background: 'var(--color-bg)',
              boxShadow: 'var(--shadow-md)', position: 'relative',
            }}
          >
            <VenueMap
              venue={bundle.venue}
              level={state.mapLevel}
              highlight={state.mapZone}
              height={330}
              onSelect={handleSelect}
            />
          </div>

          <div
            className="scroll"
            style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '11px -20px 0', padding: '0 20px 2px' }}
          >
            {zoneKeys.map((z) => (
              <button
                key={z || 'all'}
                type="button"
                onClick={() => dispatch({ type: 'setMapZone', zone: z })}
                aria-pressed={state.mapZone === z}
                style={pill(state.mapZone === z)}
              >
                {z === '' ? 'Whole floor' : bundle.venue.zones[z].label}
              </button>
            ))}
          </div>

          {state.booth ? (
            <div
              style={{
                marginTop: 13, padding: 15, borderRadius: 10,
                background: 'var(--color-accent-900)', border: '1px solid var(--color-accent-700)',
              }}
            >
              <div style={kicker(9.5, 'var(--color-accent-300)', 0.14)}>
                {state.booth.zoneLabel}
              </div>
              <div style={{ font: '500 17px/1.2 var(--font-body)', margin: '8px 0 6px' }}>
                {state.booth.name}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: t(0.65) }}>{state.booth.note}</div>
              <div style={{ fontSize: 11, color: t(0.42), marginTop: 8 }}>
                {state.booth.kind} · Level {state.mapLevel}
              </div>
            </div>
          ) : (
            <div style={{ ...emptyBox, marginTop: 13 }}>{bundle.strings.mapEmpty}</div>
          )}
          <div style={{ height: 26 }} />
        </div>
      ) : (
        <div>
          <div
            className="scroll"
            style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -20px 14px', padding: '2px 20px' }}
          >
            {bundle.exhibitorZones.map((z) => (
              <button
                key={z.key}
                type="button"
                onClick={() => dispatch({ type: 'setExZone', zone: z.label })}
                aria-pressed={state.exZone === z.label}
                style={pill(state.exZone === z.label)}
              >
                {z.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            {exhibitors.map((x, i) => (
              <div
                key={x.booth}
                style={{
                  padding: '13px 14px', borderRadius: 10, background: 'var(--color-surface)',
                  boxShadow: 'var(--shadow-sm)', ...settle(i),
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <div style={{ font: '500 15px/1.2 var(--font-body)', flex: 1, minWidth: 0 }}>
                    {x.name}
                  </div>
                  <div
                    style={{
                      font: '400 11px/1 var(--font-mono)', color: 'var(--color-accent-300)', flex: 'none',
                    }}
                  >
                    {x.booth}
                  </div>
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: t(0.55), marginTop: 6 }}>{x.note}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {x.offers.map((o) => (
                    <span
                      key={o}
                      style={{
                        display: 'inline-flex', fontSize: 10, padding: '3px 10px', borderRadius: 6,
                        background: 'var(--color-neutral-800)', color: 'var(--color-neutral-100)',
                      }}
                    >
                      {o}
                    </span>
                  ))}
                  {x.exclusive && (
                    <span
                      style={{
                        display: 'inline-flex', alignItems: 'center', fontSize: 10,
                        padding: '3px 10px', borderRadius: 6,
                        border: '1px solid var(--color-accent)', color: 'var(--color-accent-300)',
                      }}
                    >
                      Exclusive · {x.exclusive}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ height: 26 }} />
        </div>
      )}
    </div>
  );
}
